import React, { useState, memo } from 'react';
import { X, ChevronRight, Mic, Wand2, BookOpen, Sparkles, Play, CheckCircle } from 'lucide-react';
type TourStep = {
  title: string;
  description: string;
  highlight?: string;
  action?: string;
};
const tourSteps: TourStep[] = [{
  title: 'Record Your Stories',
  description: 'Click the green "Record" button to start capturing your family memories. You can record audio, video, or write text.',
  highlight: 'stories-page',
  action: 'Go to Stories'
}, {
  title: 'Create a Storybook',
  description: 'Once you have stories recorded, visit the Create page to transform them into beautiful AI-illustrated storybooks.',
  highlight: 'create-page',
  action: 'Go to Create'
}, {
  title: 'View Your Creations',
  description: 'Your finished storybooks appear in the Creations page where you can read, download, and share them with family.',
  highlight: 'creations-page',
  action: 'Go to Creations'
}];
type OnboardingTourProps = {
  isOpen: boolean;
  onComplete: () => void;
  onSkip: () => void;
  onNavigate?: (page: string) => void;
  onStartInteractiveTour?: () => void;
};
export function OnboardingTour({
  isOpen,
  onComplete,
  onSkip,
  onNavigate,
  onStartInteractiveTour
}: OnboardingTourProps) {
  const [tourMode, setTourMode] = useState<'welcome' | 'tour'>('welcome');
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState<'explore' | 'tour' | null>('tour');
  if (!isOpen) return null;
  const handleStartTour = () => {
    setSelectedOption('tour');
  };
  const handleExploreSelf = () => {
    setSelectedOption('explore');
  };
  const handleContinue = () => {
    if (selectedOption === 'tour' && onStartInteractiveTour) {
      onStartInteractiveTour();
    } else if (selectedOption === 'explore') {
      onSkip();
    }
  };
  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      // Navigate to the appropriate page
      const step = tourSteps[nextStep];
      if (step.highlight === 'stories-page' && onNavigate) {
        onNavigate('stories');
      } else if (step.highlight === 'create-page' && onNavigate) {
        onNavigate('create');
      } else if (step.highlight === 'creations-page' && onNavigate) {
        onNavigate('creations');
      }
    } else {
      onComplete();
    }
  };
  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      // Navigate to the appropriate page
      const step = tourSteps[prevStep];
      if (step.highlight === 'stories-page' && onNavigate) {
        onNavigate('stories');
      } else if (step.highlight === 'create-page' && onNavigate) {
        onNavigate('create');
      } else if (step.highlight === 'creations-page' && onNavigate) {
        onNavigate('creations');
      }
    }
  };
  // Welcome Screen
  if (tourMode === 'welcome') {
    return <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-1359">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onSkip} data-id="element-1360" />

        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden" data-id="element-1361">
          {/* Close Button */}
          <button onClick={onSkip} className="absolute top-4 right-4 z-10 p-2 text-gray-400 hover:text-gray-600 bg-white rounded-full shadow-sm" data-id="element-1362">
            <X className="w-5 h-5" data-id="element-1363" />
          </button>

          {/* Content */}
          <div className="p-12 text-center" data-id="element-1364">
            {/* Icon */}
            <div className="flex justify-center mb-6" data-id="element-1365">
              <div className="w-20 h-20 bg-gradient-to-br from-green-100 to-emerald-100 rounded-2xl flex items-center justify-center" data-id="element-1366">
                <Sparkles className="w-10 h-10 text-green-600" data-id="element-1367" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-1368">
              Welcome to Folklore! 👋
            </h2>

            {/* Demo Account Notice */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-xl mb-6" data-id="element-1369">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" data-id="element-1370"></div>
              <span className="text-sm font-medium text-blue-900" data-id="element-1371">
                This is a demo account
              </span>
            </div>

            {/* Description */}
            <p className="text-lg text-gray-600 mb-8 max-w-lg mx-auto" data-id="element-1372">
              This demo showcases Folklore's features with sample data. Nothing
              you do here will be saved, but feel free to explore!
            </p>

            {/* Options */}
            <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-1373">
              <button onClick={handleExploreSelf} className={`p-6 rounded-2xl transition-all border-2 ${selectedOption === 'explore' ? 'bg-gray-100 border-gray-400 shadow-lg' : 'bg-gray-50 hover:bg-gray-100 border-gray-200 hover:border-gray-300'}`} data-id="element-1374">
                <div className="w-12 h-12 bg-gray-200 rounded-xl flex items-center justify-center mx-auto mb-3" data-id="element-1375">
                  <Play className="w-6 h-6 text-gray-600" data-id="element-1376" />
                </div>
                <div className="font-semibold text-gray-900 mb-1" data-id="element-1377">
                  Explore Yourself
                </div>
                <div className="text-sm text-gray-600" data-id="element-1378">
                  Click around and discover features at your own pace
                </div>
              </button>

              <button onClick={handleStartTour} className={`p-6 rounded-2xl transition-all border-2 ${selectedOption === 'tour' ? 'bg-gradient-to-br from-green-600 to-emerald-600 border-green-700 shadow-lg' : 'bg-gradient-to-br from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 border-green-600'}`} data-id="element-1379">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mx-auto mb-3" data-id="element-1380">
                  <Sparkles className="w-6 h-6 text-white" data-id="element-1381" />
                </div>
                <div className="font-semibold text-white mb-1" data-id="element-1382">Take a Tour</div>
                <div className="text-sm text-green-100" data-id="element-1383">
                  We'll walk you through the core workflow
                </div>
              </button>
            </div>

            {/* Continue Button */}
            <button onClick={handleContinue} disabled={!selectedOption} className={`w-full px-6 py-3 font-semibold rounded-xl transition-colors mb-4 ${selectedOption ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-1384">
              Continue
            </button>

            {/* Tour Button Hint */}
            <p className="text-sm text-gray-500" data-id="element-1385">
              You can restart this tour anytime from the{' '}
              <span className="font-medium" data-id="element-1386">?</span> button in the bottom right
              corner
            </p>
          </div>
        </div>
      </div>;
  }
  // Tour Mode
  const step = tourSteps[currentStep];
  const isLastStep = currentStep === tourSteps.length - 1;
  return <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none" data-id="element-1387">
      {/* Spotlight overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto" onClick={onSkip} data-id="element-1388" />

      {/* Tour Card */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 pointer-events-auto" data-id="element-1389">
        {/* Close Button */}
        <button onClick={onSkip} className="absolute -top-3 -right-3 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors border-2 border-gray-200" data-id="element-1390">
          <X className="w-5 h-5 text-gray-600" data-id="element-1391" />
        </button>

        {/* Content */}
        <div className="p-8" data-id="element-1392">
          {/* Step Indicator */}
          <div className="flex items-center gap-2 mb-6" data-id="element-1393">
            {tourSteps.map((_, index) => <div key={index} className={`h-1.5 rounded-full transition-all ${index === currentStep ? 'bg-green-500 w-8' : index < currentStep ? 'bg-green-300 w-6' : 'bg-gray-200 w-6'}`} data-id="element-1394" />)}
          </div>

          {/* Icon */}
          <div className="flex justify-center mb-4" data-id="element-1395">
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center" data-id="element-1396">
              {currentStep === 0 && <Mic className="w-8 h-8 text-green-600" data-id="element-1397" />}
              {currentStep === 1 && <Wand2 className="w-8 h-8 text-green-600" data-id="element-1398" />}
              {currentStep === 2 && <BookOpen className="w-8 h-8 text-green-600" data-id="element-1399" />}
            </div>
          </div>

          {/* Title */}
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-3" data-id="element-1400">
            {step.title}
          </h3>

          {/* Description */}
          <p className="text-gray-600 text-center mb-6" data-id="element-1401">{step.description}</p>

          {/* Progress */}
          <div className="text-center text-sm text-gray-500 mb-6" data-id="element-1402">
            Step {currentStep + 1} of {tourSteps.length}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between gap-3" data-id="element-1403">
            <button onClick={handlePrevious} disabled={currentStep === 0} className={`px-6 py-3 font-medium rounded-xl transition-colors ${currentStep === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`} data-id="element-1404">
              Previous
            </button>

            <button onClick={onSkip} className="px-6 py-3 text-gray-600 font-medium hover:text-gray-900 transition-colors" data-id="element-1405">
              Skip Tour
            </button>

            <button onClick={handleNext} className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-medium rounded-xl hover:bg-green-600 transition-colors" data-id="element-1406">
              {isLastStep ? <>
                  <CheckCircle className="w-5 h-5" data-id="element-1407" />
                  Finish Tour
                </> : <>
                  Next
                  <ChevronRight className="w-5 h-5" data-id="element-1408" />
                </>}
            </button>
          </div>
        </div>
      </div>
    </div>;
}